import type { SeoData, JsonLd } from "@/lib/seo";
import { SITE_URL, SITE_NAME, FAVICON_URL } from "@/lib/seo";
import { cssbuySpreadsheetContent } from "@/mocks/cssbuy-spreadsheet-content";
import { cssbuyReviewContent } from "@/mocks/cssbuy-review-content";
import { pillarArticles } from "@/mocks/pillar-articles";

type Lang = "en" | "pt-BR" | "de";

const DEFAULT_KEYWORDS =
  "CSSBuy Spreadsheet, CSSBuy review, CSSBuy shipping, CSSBuy coupon, CSSBuy finds, CSSBuy sellers, CSSBuy QC, CSSBuy warehouse, CSSBuy guide, CSSBuy products";

interface ArticleLike {
  title: string;
  metaDescription: string;
  h1: string;
  published: string;
  updated: string;
  faq: { q: string; a: string }[];
  image?: string;
}

export interface RouteSeo {
  seo: SeoData;
  schemas: JsonLd[];
}

const staticPages: Record<string, SeoData> = {
  "/cssbuy-shipping/": {
    title: "CSSBuy Shipping Cost Guide 2026 — Lines, Prices & Tips",
    description:
      "Complete CSSBuy shipping cost guide for 2026. Compare shipping lines, understand weight and volume pricing, and learn how to reduce international shipping costs.",
    keywords:
      "CSSBuy shipping cost, CSSBuy shipping, CSSBuy shipping lines, CSSBuy EMS, CSSBuy DHL, CSSBuy shipping guide",
    canonicalPath: "/cssbuy-shipping/",
    ogType: "article",
  },
  "/cssbuy-coupon/": {
    title: "CSSBuy Coupon & Discount Code Guide 2026",
    description:
      "Find CSSBuy coupons, discount codes, and promotions. Learn how to verify validity and maximize savings on your CSSBuy purchases in 2026.",
    keywords: "CSSBuy coupon, CSSBuy discount code, CSSBuy promotion, CSSBuy deals, CSSBuy savings",
    canonicalPath: "/cssbuy-coupon/",
    ogType: "article",
  },
  "/cssbuy-warehouse/": {
    title: "CSSBuy Warehouse Guide 2026 — Storage, QC & Shipping",
    description:
      "Understand how the CSSBuy warehouse works, including storage, consolidation, quality control photos, and preparing items for international shipping.",
    keywords: "CSSBuy warehouse, CSSBuy storage, CSSBuy QC, CSSBuy consolidation, CSSBuy shipping",
    canonicalPath: "/cssbuy-warehouse/",
    ogType: "article",
  },
  "/cssbuy-safety/": {
    title: "CSSBuy Safety Guide 2026 — Avoid Scams & Buy Safe",
    description:
      "Learn how to buy safely through CSSBuy. Identify red flags, verify sellers, check QC photos, and avoid scams with our independent safety guide.",
    keywords: "CSSBuy safety, CSSBuy safe, CSSBuy scam, CSSBuy legit, CSSBuy seller verification",
    canonicalPath: "/cssbuy-safety/",
    ogType: "article",
  },
  "/comparisons/": {
    title: "CSSBuy Comparisons — Superbuy, Sugargoo & AllChinaBuy",
    description:
      "Compare CSSBuy with Superbuy, Sugargoo, AllChinaBuy and other buying agents. Find the best agent based on fees, features, and shipping.",
    keywords: "CSSBuy vs Superbuy, CSSBuy vs Sugargoo, CSSBuy vs AllChinaBuy, CSSBuy comparisons",
    canonicalPath: "/comparisons/",
    ogType: "website",
  },
  "/blog/": {
    title: "CSSBuy Blog — Guides, Reviews & Buying Tips",
    description:
      "The CSSBuy Spreadsheet blog featuring buying guides, reviews, shipping tips, QC advice, and the latest CSSBuy finds for international shoppers.",
    keywords: "CSSBuy blog, CSSBuy guides, CSSBuy tips, CSSBuy buying guide, CSSBuy articles",
    canonicalPath: "/blog/",
    ogType: "website",
  },
  "/about/": {
    title: "About Us — CSSBuy Spreadsheet Resource Hub",
    description:
      "Learn about CSSBuy Spreadsheet, an independent resource hub providing buying guides, product finds, seller information, and shipping resources for CSSBuy users.",
    keywords: "CSSBuy Spreadsheet, CSSBuy resource, CSSBuy about, independent CSSBuy guide",
    canonicalPath: "/about/",
    ogType: "website",
  },
  "/editorial-policy/": {
    title: "Editorial Policy — CSSBuy Spreadsheet",
    description:
      "Our editorial policy explains how we create, review, and update CSSBuy Spreadsheet content to ensure accuracy, transparency, and independence.",
    keywords: "CSSBuy editorial policy, CSSBuy content policy, CSSBuy accuracy",
    canonicalPath: "/editorial-policy/",
    ogType: "website",
  },
  "/contact/": {
    title: "Contact Us — CSSBuy Spreadsheet",
    description:
      "Get in touch with the CSSBuy Spreadsheet team. Report issues, suggest updates, or ask questions about our CSSBuy buying guides and resources.",
    keywords: "CSSBuy contact, CSSBuy Spreadsheet contact, CSSBuy support",
    canonicalPath: "/contact/",
    ogType: "website",
  },
};

function websiteSchema(url: string): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url,
    inLanguage: ["en", "pt-BR", "de"],
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      url,
      logo: FAVICON_URL,
    },
  };
}

function organizationSchema(url: string): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url,
    logo: FAVICON_URL,
  };
}

function webPageSchema(seo: SeoData): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: seo.title,
    description: seo.description,
    url: `${SITE_URL}${seo.canonicalPath}`,
    inLanguage: "en",
    isPartOf: { "@type": "WebSite", name: SITE_NAME, url: `${SITE_URL}/` },
  };
}

function breadcrumbSchema(items: { name: string; path: string }[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

function articleSchema(article: ArticleLike, seo: SeoData, lang: Lang): Record<string, unknown> {
  const data: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: seo.title,
    description: seo.description,
    mainEntityOfPage: `${SITE_URL}${seo.canonicalPath}`,
    author: { "@type": "Organization", name: SITE_NAME, url: `${SITE_URL}/` },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: FAVICON_URL },
    },
    datePublished: article.published,
    dateModified: article.updated,
    inLanguage: lang === "pt-BR" ? "pt-BR" : lang,
  };
  if (article.image) {
    data.image = article.image;
  }
  return data;
}

function faqSchema(faq: { q: string; a: string }[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

function buildArticleRoute(path: string, article: ArticleLike, lang: Lang): RouteSeo {
  const seo: SeoData = {
    title: article.title,
    description: article.metaDescription,
    keywords: DEFAULT_KEYWORDS,
    canonicalPath: path,
    ogType: "article",
  };

  const schemas: JsonLd[] = [
    {
      id: "breadcrumb",
      data: breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: article.h1, path },
      ]),
    },
    { id: "article", data: articleSchema(article, seo, lang) },
  ];

  if (article.faq && article.faq.length > 0) {
    schemas.push({ id: "faq", data: faqSchema(article.faq) });
  }

  return { seo, schemas };
}

function buildStaticRoute(path: string, seo: SeoData): RouteSeo {
  const breadcrumbName = path
    .replace(/^\/+/, "")
    .replace(/\/+$/, "")
    .split("-")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

  return {
    seo,
    schemas: [
      {
        id: "breadcrumb",
        data: breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: breadcrumbName, path },
        ]),
      },
      { id: "webpage", data: webPageSchema(seo) },
    ],
  };
}

export function getRouteSeo(pathname: string, lang: Lang): RouteSeo {
  const path = pathname === "/" ? "/" : pathname;

  // Home
  if (path === "/") {
    const seo: SeoData = {
      title: "CSSBuy Spreadsheet 2026 — Best Finds, Sellers & Buying Guides",
      description:
        "CSSBuy Spreadsheet 2026 — Find the best products, sellers, categories, QC guides, shipping information, coupons and buying resources for CSSBuy. Independent resource hub.",
      keywords: DEFAULT_KEYWORDS,
      canonicalPath: "/",
      ogType: "website",
    };
    return {
      seo,
      schemas: [
        { id: "website", data: websiteSchema(`${SITE_URL}/`) },
        { id: "organization", data: organizationSchema(`${SITE_URL}/`) },
        {
          id: "breadcrumb",
          data: breadcrumbSchema([{ name: "Home", path: "/" }]),
        },
      ],
    };
  }

  // Spreadsheet guide
  if (path === "/cssbuy-spreadsheet/") {
    const c = cssbuySpreadsheetContent[lang] || cssbuySpreadsheetContent.en;
    return buildArticleRoute(path, c, lang);
  }

  // Review
  if (path === "/cssbuy-review/") {
    const c = cssbuyReviewContent[lang] || cssbuyReviewContent.en;
    return buildArticleRoute(path, c, lang);
  }

  // Pillar articles
  const slug = path.replace(/^\/+/, "").replace(/\/+$/, "");
  if (pillarArticles[slug]) {
    const a = pillarArticles[slug];
    const c = a[lang] || a.en;
    return buildArticleRoute(path, { ...c, image: a.image }, lang);
  }

  // Static / placeholder pages
  const staticPage = staticPages[path];
  if (staticPage) {
    return buildStaticRoute(path, staticPage);
  }

  // Fallback for unknown / 404 routes
  return {
    seo: {
      title: SITE_NAME,
      description:
        "CSSBuy Spreadsheet — independent buying guides, product finds, seller information, and shipping resources.",
      keywords: DEFAULT_KEYWORDS,
      canonicalPath: "/",
      ogType: "website",
    },
    schemas: [],
  };
}